import React, { useEffect, useState } from "react";
import styles from "./Employee.module.css";
import { STAFF_STATUS_OPTIONS, PF_ESI_RATES } from "./HrmData";
import CheckBox from "../../components/updatedComponents/FormComponents/CheckBox";
import DropDown2 from "../../components/updatedComponents/FormComponents/Dropdown2";

const emptyForm = {
  name: "",
  mobile: "",
  department: "",
  designation: "",
  joiningDate: "",
  ctc: "",
  monthlyGross: "",
  basicSalary: "",
  monthlySalary: "",
  pfApplicable: true,
  esiApplicable: true,
  status: "Active",
};

const EmployeeFormModal = ({ mode, initialStaff, onClose, onSave }) => {
  const isEdit = Boolean(initialStaff);
  const [form, setForm] = useState(
    initialStaff
      ? {
          name: initialStaff.name || "",
          mobile: initialStaff.mobile || "",
          department: initialStaff.department || "",
          designation: initialStaff.designation || "",
          joiningDate: initialStaff.joiningDate || "",
          ctc: initialStaff.ctc ?? "",
          monthlyGross: initialStaff.monthlyGross ?? "",
          basicSalary: initialStaff.basicSalary ?? "",
          monthlySalary: initialStaff.monthlySalary ?? "",
          pfApplicable: initialStaff.pfApplicable ?? true,
          esiApplicable: initialStaff.esiApplicable ?? true,
          status: initialStaff.status || "Active",
        }
      : emptyForm,
  );

  const [pfEsiPreview, setPfEsiPreview] = useState({
    pfEmployee: 0,
    pfEmployer: 0,
    esiEmployee: 0,
    esiEmployer: 0,
  });

  // Recompute the auto PF/ESI figures whenever salary or applicability changes.
  useEffect(() => {
    const base = Number(form.monthlySalary) || 0;
    setPfEsiPreview({
      pfEmployee: form.pfApplicable
        ? Math.round((base * PF_ESI_RATES.pfEmployeePercent) / 100)
        : 0,
      pfEmployer: form.pfApplicable
        ? Math.round((base * PF_ESI_RATES.pfEmployerPercent) / 100)
        : 0,
      esiEmployee: form.esiApplicable
        ? Math.round((base * PF_ESI_RATES.esiEmployeePercent) / 100)
        : 0,
      esiEmployer: form.esiApplicable
        ? Math.round((base * PF_ESI_RATES.esiEmployerPercent) / 100)
        : 0,
    });
  }, [form.monthlySalary, form.pfApplicable, form.esiApplicable]);

  const update = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = () => {
    if (!form.name.trim() || !form.department.trim() || !form.joiningDate) {
      return;
    }

    onSave({
      ...form,
      name: form.name.trim(),
      department: form.department.trim(),
      designation: form.designation.trim(),
      mobile: form.mobile.trim(),
      ctc: Number(form.ctc) || 0,
      monthlyGross: Number(form.monthlyGross) || 0,
      basicSalary: Number(form.basicSalary) || 0,
      monthlySalary: Number(form.monthlySalary) || 0,
    });
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={`${styles.modalCard} ${mode ? styles.modalCardDark : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className={styles.modalCloseBtn}
          onClick={onClose}
        >
          &times;
        </button>

        {/* <p className={styles.modalEyebrow}>STAFF</p> */}
        <h2 className={styles.modalTitle}>
          {isEdit ? "Edit Employee" : "Add Employee"}
        </h2>

        <div className={styles.formGrid}>
          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>Employee ID</span>
            <input
              type="text"
              className={`${styles.textInput} ${mode ? styles.textInputDark : ""}`}
              value={isEdit ? initialStaff.staffId : "Auto-generated"}
              disabled
            />
          </div>

          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>Name</span>
            <input
              type="text"
              className={`${styles.textInput} ${mode ? styles.textInputDark : ""}`}
              placeholder="Employee name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
            />
          </div>

          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>Mobile</span>
            <input
              type="text"
              className={`${styles.textInput} ${mode ? styles.textInputDark : ""}`}
              placeholder="Mobile number"
              value={form.mobile}
              onChange={(e) => update("mobile", e.target.value)}
            />
          </div>

          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>Department</span>
            <input
              type="text"
              className={`${styles.textInput} ${mode ? styles.textInputDark : ""}`}
              placeholder="Production"
              value={form.department}
              onChange={(e) => update("department", e.target.value)}
            />
          </div>

          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>Designation</span>
            <input
              type="text"
              className={`${styles.textInput} ${mode ? styles.textInputDark : ""}`}
              placeholder="Operator"
              value={form.designation}
              onChange={(e) => update("designation", e.target.value)}
            />
          </div>

          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>Joining Date</span>
            <input
              type="date"
              className={`${styles.textInput} ${mode ? styles.textInputDark : ""}`}
              value={form.joiningDate}
              onChange={(e) => update("joiningDate", e.target.value)}
            />
          </div>

          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>CTC</span>
            <input
              type="number"
              min="0"
              className={`${styles.textInput} ${mode ? styles.textInputDark : ""}`}
              placeholder="342000"
              value={form.ctc}
              onChange={(e) => update("ctc", e.target.value)}
            />
          </div>

          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>Monthly Gross</span>
            <input
              type="number"
              min="0"
              className={`${styles.textInput} ${mode ? styles.textInputDark : ""}`}
              placeholder="28500"
              value={form.monthlyGross}
              onChange={(e) => update("monthlyGross", e.target.value)}
            />
          </div>

          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>Basic Salary</span>
            <input
              type="number"
              min="0"
              className={`${styles.textInput} ${mode ? styles.textInputDark : ""}`}
              placeholder="11400"
              value={form.basicSalary}
              onChange={(e) => update("basicSalary", e.target.value)}
            />
          </div>

          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>Monthly Salary</span>
            <input
              type="number"
              min="0"
              className={`${styles.textInput} ${mode ? styles.textInputDark : ""}`}
              placeholder="28000"
              value={form.monthlySalary}
              onChange={(e) => update("monthlySalary", e.target.value)}
            />
          </div>

          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>PF employee</span>
            <input
              type="text"
              className={`${styles.textInput} ${mode ? styles.textInputDark : ""}`}
              value={form.pfApplicable ? `₹${pfEsiPreview.pfEmployee}` : "N/A"}
              disabled
            />
          </div>

          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>PF employer</span>
            <input
              type="text"
              className={`${styles.textInput} ${mode ? styles.textInputDark : ""}`}
              value={form.pfApplicable ? `₹${pfEsiPreview.pfEmployer}` : "N/A"}
              disabled
            />
          </div>

          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>ESI employee</span>
            <input
              type="text"
              className={`${styles.textInput} ${mode ? styles.textInputDark : ""}`}
              value={
                form.esiApplicable ? `₹${pfEsiPreview.esiEmployee}` : "N/A"
              }
              disabled
            />
          </div>

          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>ESI employer</span>
            <input
              type="text"
              className={`${styles.textInput} ${mode ? styles.textInputDark : ""}`}
              value={
                form.esiApplicable ? `₹${pfEsiPreview.esiEmployer}` : "N/A"
              }
              disabled
            />
          </div>

          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>Status</span>
            <DropDown2
              label="Status"
              listLabel="Select Status"
              options={STAFF_STATUS_OPTIONS.map((s) => ({
                name: s,
                value: s,
              }))}
              onSelect={(option) => update("status", option.value)}
            />
          </div>

          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>Applicability</span>
            <div className={styles.checkRow}>
              <label className={styles.checkLabel}>
                <CheckBox
                  type="checkbox"
                  checked={form.pfApplicable}
                  handleChange={(e) => update("pfApplicable", e.target.checked)}
                />
                PF Applicable
              </label>
              <label className={styles.checkLabel}>
                <CheckBox
                  type="checkbox"
                  checked={form.esiApplicable}
                  handleChange={(e) =>
                    update("esiApplicable", e.target.checked)
                  }
                />
                ESI Applicable
              </label>
            </div>
          </div>
        </div>

        <div className={styles.modalActions}>
          <button type="button" className={styles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.saveBtn}
            onClick={handleSubmit}
          >
            {isEdit ? "Save Changes" : "Save Employee"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeFormModal;
